import { useState, useRef, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ExpandMore from "@mui/icons-material/ExpandMore";

export default function DropdownPopover({
  buttonLabel,
  children,
  tooltipContent,
  width = 150,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const handleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prev) => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the dropdown content
      if (containerRef.current && containerRef.current.contains(event.target)) {
        return;
      }

      setOpen(false);
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  // Check if the button label indicates active filters
  const hasActiveFilters =
    buttonLabel &&
    (buttonLabel.includes("selected") ||
      buttonLabel.includes("All") ||
      (buttonLabel !== "Team" && buttonLabel !== "Manager"));

  const buttonContent = (
    <Button
      ref={buttonRef}
      onClick={handleClick}
      variant="outlined"
      sx={{
        borderRadius: 5,
        textTransform: "capitalize",
        backgroundColor: hasActiveFilters ? "#e3f2fd" : "transparent",
        borderColor: hasActiveFilters ? "#2196f3" : undefined,
        color: hasActiveFilters ? "#1976d2" : undefined,
        "&:hover": {
          backgroundColor: hasActiveFilters ? "#bbdefb" : undefined,
          borderColor: hasActiveFilters ? "#1976d2" : undefined,
        },
        minWidth: 120,
        maxWidth: 150,
        height: 40,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        width: width,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {buttonLabel}
        </span>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          ml: 0.5,
          flexShrink: 0,
        }}
      >
        <ExpandMore />
      </Box>
    </Button>
  );

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
      {/* Button to open the dropdown with optional tooltip */}
      {tooltipContent && hasActiveFilters ? (
        <Tooltip title={tooltipContent} placement="bottom" arrow>
          {buttonContent}
        </Tooltip>
      ) : (
        buttonContent
      )}

      {/* Custom positioned dropdown */}
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1300,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            border: "1px solid rgba(0,0,0,0.12)",
            width: width,
            mt: 1, // Small margin from button
          }}
        >
          <Box p={2}>{children}</Box>
        </Box>
      )}
    </Box>
  );
}
